import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  Node,
  Edge,
  Connection,
  addEdge,
  useNodesState,
  useEdgesState,
  ConnectionMode,
  ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Mail, Clock, GitBranch, Filter, Play } from 'lucide-react';
import SendEmailModal from '../NodeModals/SendEmailModal';

const FlowCanvasContainer = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.background};
  position: relative;
  
  .react-flow {
    background: ${({ theme }) => theme.colors.background};
  }
  
  .react-flow__node {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    border: 2px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.lg};
    padding: ${({ theme }) => theme.spacing[4]};
    font-family: ${({ theme }) => theme.typography.fontFamily.primary};
    color: ${({ theme }) => theme.colors.textPrimary};
    min-width: 180px;
    transition: all ${({ theme }) => theme.animations.duration.fast};
    
    &:hover {
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: ${({ theme }) => theme.shadows.lg};
    }
    
    &.selected {
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.primary}30;
    }
  }
  
  .react-flow__edge {
    stroke: ${({ theme }) => theme.colors.border};
    stroke-width: 2px;
    
    &.selected {
      stroke: ${({ theme }) => theme.colors.primary};
    }
  }
  
  .react-flow__edge-path {
    stroke: ${({ theme }) => theme.colors.border};
    stroke-width: 2px;
  }
  
  .react-flow__handle {
    background: ${({ theme }) => theme.colors.primary};
    width: 10px;
    height: 10px;
    border: 2px solid ${({ theme }) => theme.colors.backgroundSecondary};
    
    &:hover {
      background: ${({ theme }) => theme.colors.primaryHover};
    }
  }
  
  .react-flow__controls {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.lg};
    
    button {
      background: transparent;
      border: none;
      color: ${({ theme }) => theme.colors.textSecondary};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
      
      &:hover {
        background: ${({ theme }) => theme.colors.primary}20;
        color: ${({ theme }) => theme.colors.primary};
      }
      
      &:last-child {
        border-bottom: none;
      }
    }
  }
  
  .react-flow__minimap {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.md};
  }
  
  .react-flow__attribution {
    display: none;
  }
`;

const NodeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const NodeHeader = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  
  svg {
    color: ${({ $color }) => $color};
  }
`;

const NodeTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const NodeDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
`;

const NodeBadge = styled.div<{ $color?: string }>`
  display: inline-block;
  background: ${({ $color }) => $color}20;
  color: ${({ $color }) => $color};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borders.radius.full};
`;

interface FlowCanvasProps {
  flowId: string;
  channel: string;
  userId: string;
}

const nodeTypes = {
  trigger: {
    icon: Play,
    color: '#00ff88',
    label: 'Trigger',
  },
  sendEmail: {
    icon: Mail,
    color: '#ff3b30',
    label: 'Enviar Email',
  },
  delay: {
    icon: Clock,
    color: '#ffaa00',
    label: 'Delay',
  },
  condition: {
    icon: GitBranch,
    color: '#00aaff',
    label: 'Condição',
  },
  loop: {
    icon: Filter,
    color: '#ff6259',
    label: 'Loop',
  },
};

const CustomNode: React.FC<{ data: any }> = ({ data }) => {
  const nodeType = nodeTypes[data.type as keyof typeof nodeTypes] || nodeTypes.trigger;
  const Icon = nodeType.icon;
  
  return (
    <NodeContent>
      <NodeHeader $color={nodeType.color}>
        <Icon size={20} />
        <NodeTitle>{data.label}</NodeTitle>
      </NodeHeader>
      
      {data.description && (
        <NodeDescription>{data.description}</NodeDescription>
      )}
      
      {data.status && (
        <NodeBadge $color={nodeType.color}>
          {data.status}
        </NodeBadge>
      )}
    </NodeContent>
  );
};

const FlowCanvas: React.FC<FlowCanvasProps> = ({ flowId, channel, userId }) => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = React.useState<ReactFlowInstance | null>(null);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const initialNodes: Node[] = useMemo(() => [
    {
      id: 'trigger-1',
      type: 'default',
      position: { x: 250, y: 50 },
      data: {
        label: 'Trigger Manual',
        type: 'trigger',
        description: 'Inicia o fluxo manualmente',
        status: 'Configurado',
      },
    },
    {
      id: 'sendEmail-1',
      type: 'default',
      position: { x: 250, y: 200 },
      data: {
        label: 'Enviar Email de Boas-vindas',
        type: 'sendEmail',
        description: 'Email: {{lead.email}}',
      },
    },
  ], []);

  const initialEdges: Edge[] = useMemo(() => [
    {
      id: 'e-trigger-sendEmail',
      source: 'trigger-1',
      target: 'sendEmail-1',
      animated: true,
    },
  ], []);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges((eds) => addEdge(connection, eds));
    },
    [setEdges]
  );

  // Handler para drop de nodes do NodePalette
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      if (!reactFlowWrapper.current || !reactFlowInstance) {
        return;
      }

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const data = event.dataTransfer.getData('application/reactflow');
      
      if (!data) {
        return;
      }

      const nodeData = JSON.parse(data);
      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode: Node = {
        id: `${nodeData.type}-${Date.now()}`,
        type: 'default',
        position,
        data: {
          label: nodeData.label,
          type: nodeData.type,
          ...nodeData.config,
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes]
  );

  // Handler para double-click em nodes
  const onNodeDoubleClick = useCallback((_event: React.MouseEvent, node: Node) => {
    console.log('🖱️ Double-click no node:', node.data.type, node);
    setSelectedNode(node);
    setIsModalOpen(true);
  }, []);

  // Handler para salvar configuração do modal
  const handleSaveNodeConfig = useCallback((config: { to: string; subject: string; body: string }) => {
    if (!selectedNode) return;
    
    setNodes((nds) =>
      nds.map((node) =>
        node.id === selectedNode.id
          ? {
              ...node,
              data: {
                ...node.data,
                ...config,
                description: `Para: ${config.to}`,
              },
            }
          : node
      )
    );
    
    console.log('✅ Configuração salva:', config);
  }, [selectedNode, setNodes]);

  return (
    <FlowCanvasContainer ref={reactFlowWrapper}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={setReactFlowInstance}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onNodeDoubleClick={onNodeDoubleClick}
        connectionMode={ConnectionMode.Loose}
        fitView
        attributionPosition="bottom-left"
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="#333"
        />
        <Controls />
        <MiniMap
          nodeColor={(node) => {
            const nodeType = nodeTypes[node.data.type as keyof typeof nodeTypes];
            return nodeType?.color || '#999';
          }}
          maskColor="rgba(0, 0, 0, 0.8)"
        />
      </ReactFlow>
      
      {/* Modal de Configuração SendEmail */}
      {selectedNode?.data.type === 'sendEmail' && (
        <SendEmailModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          nodeData={selectedNode.data}
          onSave={handleSaveNodeConfig}
        />
      )}
    </FlowCanvasContainer>
  );
};

export default FlowCanvas;
